// Google Drive 連携（PWA・受け取り側）。PCが Drive の Immerso フォルダへ上げた
// catalog.json / メディア / 字幕バンドルを drive.file スコープで読む。
//
// 認証は Google Identity Services（GIS）のトークンモデル＝ブラウザで access token を得る方式
// （PC=デスクトップOAuth, PWA=ウェブOAuth と別クライアントだが、同一 Google Cloud プロジェクトなら
//  drive.file で PC が作ったファイルを読める、という前提。これを実機で検証する）。
// Swift 版は別実装（ASWebAuthenticationSession＋URLSession）。[[PWA-TO-SWIFT]]
//
// Client ID（ウェブ型）は端末の IndexedDB(kv) に保存（公開されても安全な値だが、環境ごとに入れ替えたいため）。
import { kvGet, kvSet, kvDel } from './db.js'

const SCOPE = 'https://www.googleapis.com/auth/drive.file'
const GIS_SRC = 'https://accounts.google.com/gsi/client'
const CLIENT_ID_KEY = 'gdriveClientId'
// 本番の埋め込み Client ID（ウェブ型・公開されても安全＝承認済みJS生成元で保護される）。
// ユーザーは入力不要で「連携する」だけ。kv に値があればそれを優先（開発時の差し替え用に残す）。
const DEFAULT_CLIENT_ID = '614060474526-9us1j8b7900ag6kldgm5iucjpqmis35g.apps.googleusercontent.com'

// ---- Client ID（ウェブOAuthクライアント）----
export async function getClientId() {
  return (await kvGet(CLIENT_ID_KEY)) || DEFAULT_CLIENT_ID
}
export async function setClientId(id) {
  return kvSet(CLIENT_ID_KEY, (id || '').trim())
}
export async function clearClientId() {
  return kvDel(CLIENT_ID_KEY)
}

// ---- GIS スクリプトの遅延ロード ----
let _gisLoaded = null
function loadGIS() {
  if (_gisLoaded) return _gisLoaded
  _gisLoaded = new Promise((resolve, reject) => {
    if (window.google && window.google.accounts && window.google.accounts.oauth2) return resolve()
    const s = document.createElement('script')
    s.src = GIS_SRC
    s.async = true
    s.defer = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('Google ログインの読み込みに失敗しました'))
    document.head.appendChild(s)
  })
  return _gisLoaded
}

// ---- アクセストークン（メモリ保持・短命）＋トークンクライアント（サイレント更新用に保持） ----
let _token = null // { access_token, expiry(ms) }
let _tokenClient = null

export function isConnected() {
  return !!_token && _token.expiry > Date.now()
}

// トークンクライアントを用意（client_id が変わったら作り直し）。callback は要求ごとに差し替える。
async function ensureTokenClient() {
  await loadGIS()
  const clientId = await getClientId()
  if (!clientId) throw new Error('Google の Client ID が未設定です（設定で入力してください）')
  if (!_tokenClient || _tokenClient._clientId !== clientId) {
    _tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: SCOPE,
      callback: () => {}, // requestAccessToken のたびに差し替える
    })
    _tokenClient._clientId = clientId
  }
  return _tokenClient
}

// アクセストークンを要求。prompt='consent'＝同意画面を出す（初回）、prompt=''＝サイレント
// （同意済みなら画面なしで更新。失敗時は reject＝再連携が必要）。
function requestToken(prompt) {
  return new Promise((resolve, reject) => {
    ensureTokenClient()
      .then((tc) => {
        tc.callback = (resp) => {
          if (resp.error) return reject(new Error(resp.error))
          _token = {
            access_token: resp.access_token,
            expiry: Date.now() + (resp.expires_in || 3600) * 1000 - 60000, // 1分マージン
          }
          resolve({ ok: true })
        }
        try {
          tc.requestAccessToken({ prompt })
        } catch (e) {
          reject(e)
        }
      })
      .catch(reject)
  })
}

// 対話的に連携（初回・同意画面を出す）。
export async function connect() {
  return requestToken('consent')
}

export function disconnect() {
  _token = null
}

// 有効なトークンを返す。期限切れ/未連携なら「サイレント更新」を試み、ダメなら再連携を促す。
// 同一セッション中の1時間ごとの失効は画面なしで更新できる（GISトークンモデル）。
async function token() {
  if (isConnected()) return _token.access_token
  try {
    await requestToken('') // サイレント（同意済みなら画面なしで更新）
    return _token.access_token
  } catch (e) {
    throw new Error('Google Drive の再連携が必要です（設定から連携し直してください）')
  }
}

// ---- Drive REST（drive.file） ----
// q に一致するファイル一覧 [{id,name,size,mimeType}]。
async function driveList(q) {
  const t = await token()
  const url =
    'https://www.googleapis.com/drive/v3/files?q=' +
    encodeURIComponent(q) +
    '&fields=' +
    encodeURIComponent('files(id,name,size,mimeType,modifiedTime)') +
    '&spaces=drive&pageSize=1000'
  const r = await fetch(url, { headers: { Authorization: 'Bearer ' + t } })
  if (!r.ok) throw new Error('Drive 一覧の取得に失敗: ' + r.status)
  return (await r.json()).files || []
}

// fileId の中身を取得する fetch Response（alt=media）。stream/blob/json は呼び出し側で。
async function driveGetResponse(fileId) {
  const t = await token()
  const r = await fetch('https://www.googleapis.com/drive/v3/files/' + fileId + '?alt=media', {
    headers: { Authorization: 'Bearer ' + t },
  })
  if (!r.ok) throw new Error('Drive ダウンロードに失敗: ' + r.status)
  return r
}

function escapeQ(s) {
  return String(s).replace(/\\/g, '\\\\').replace(/'/g, "\\'")
}

// name のファイルを1つ探す（無ければ null）。
export async function findFile(name) {
  const files = await driveList("name='" + escapeQ(name) + "' and trashed=false")
  return files[0] || null
}

// PCが上げた catalog.json を取得（{ schemaVersion, items } / 無ければ _empty）。
export async function fetchCatalog() {
  const files = await driveList("name='catalog.json' and trashed=false")
  if (!files.length) return { schemaVersion: 1, items: {}, _empty: true }
  const r = await driveGetResponse(files[0].id)
  const c = await r.json()
  return { schemaVersion: c.schemaVersion || 1, items: c.items || {} }
}

// PCが上げた folders.json を取得（フォルダ構成 [{id,name,parentId,order}]）。無ければ []。
export async function fetchFolders() {
  const files = await driveList("name='folders.json' and trashed=false")
  if (!files.length) return []
  const r = await driveGetResponse(files[0].id)
  const doc = await r.json()
  return Array.isArray(doc.folders) ? doc.folders : []
}

// folders.json を {folders, updatedAt} で取得（whole-doc LWW 同期用）。無ければ updatedAt:null。
export async function fetchFoldersDoc() {
  const files = await driveList("name='folders.json' and trashed=false")
  if (!files.length) return { folders: [], updatedAt: null }
  const r = await driveGetResponse(files[0].id)
  const doc = await r.json()
  return { folders: Array.isArray(doc.folders) ? doc.folders : [], updatedAt: doc.updatedAt || null }
}

// name のファイルをダウンロードする Response（メディア=stream、bundle=json 用）。
export async function getResponseByName(name) {
  const f = await findFile(name)
  if (!f) throw new Error('Drive にファイルがありません: ' + name)
  return driveGetResponse(f.id)
}

// name のファイルを JSON で取得（無ければ NotFound）。学習データの双方向同期(pull)用。
export async function getJSON(name) {
  const f = await findFile(name)
  if (!f) {
    const e = new Error('NotFound: ' + name)
    e.name = 'NotFound'
    throw e
  }
  const r = await driveGetResponse(f.id)
  return r.json()
}

// ---- 書き込み（双方向同期 push 用） ----
let _folderId = null

// Immerso フォルダを確保（無ければ作る）。create 時の親に使う。
async function ensureFolder() {
  if (_folderId) return _folderId
  const files = await driveList(
    "mimeType='application/vnd.google-apps.folder' and name='Immerso' and trashed=false"
  )
  if (files.length) {
    _folderId = files[0].id
    return _folderId
  }
  const t = await token()
  const r = await fetch('https://www.googleapis.com/drive/v3/files?fields=id', {
    method: 'POST',
    headers: { Authorization: 'Bearer ' + t, 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: 'Immerso', mimeType: 'application/vnd.google-apps.folder' }),
  })
  if (!r.ok) throw new Error('Immerso フォルダ作成に失敗: ' + r.status)
  _folderId = (await r.json()).id
  return _folderId
}

// バイナリ（メディア）を name で Drive に保存（あれば更新・無ければ Immerso フォルダに作成）。
// ※ multipart で blob を丸ごと送る＝中程度サイズ向け。巨大ファイルの resumable は将来対応。
export async function uploadBlob(name, blob, mimeType) {
  const t = await token()
  const mime = mimeType || 'application/octet-stream'
  const existing = await findFile(name)
  if (existing) {
    const r = await fetch(
      'https://www.googleapis.com/upload/drive/v3/files/' + existing.id + '?uploadType=media',
      { method: 'PATCH', headers: { Authorization: 'Bearer ' + t, 'Content-Type': mime }, body: blob }
    )
    if (!r.ok) throw new Error('Drive メディア更新に失敗: ' + r.status)
    return existing.id
  }
  const folderId = await ensureFolder()
  const boundary = 'immerso_' + Date.now() + '_' + Math.floor(Math.random() * 1e9)
  const pre =
    '--' + boundary + '\r\n' +
    'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
    JSON.stringify({ name, parents: [folderId] }) + '\r\n' +
    '--' + boundary + '\r\n' +
    'Content-Type: ' + mime + '\r\n\r\n'
  const body = new Blob([pre, blob, '\r\n--' + boundary + '--'])
  const r = await fetch(
    'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id',
    {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + t, 'Content-Type': 'multipart/related; boundary=' + boundary },
      body,
    }
  )
  if (!r.ok) throw new Error('Drive メディア作成に失敗: ' + r.status)
  return (await r.json()).id
}

// name のファイルを Drive から削除（無ければ何もしない）。drive.file はアプリ作成物なので削除可。
export async function deleteByName(name) {
  const f = await findFile(name)
  if (!f) return false
  const t = await token()
  const r = await fetch('https://www.googleapis.com/drive/v3/files/' + f.id, {
    method: 'DELETE',
    headers: { Authorization: 'Bearer ' + t },
  })
  if (!r.ok && r.status !== 404) throw new Error('Drive 削除に失敗: ' + r.status)
  return true
}

// ファイルの head リビジョンID（内容更新ごとに変わる＝競合検知に使う）。取れなければ null。
async function fileHeadRev(fileId) {
  const t = await token()
  const r = await fetch(
    'https://www.googleapis.com/drive/v3/files/' + fileId + '?fields=headRevisionId',
    { headers: { Authorization: 'Bearer ' + t } }
  )
  if (!r.ok) return null
  return (await r.json()).headRevisionId || null
}

// 多ライター安全な JSON 更新：最新を読む→mergeFn で更新→書く直前に版(headRevisionId)を再確認→
// 読んでから変わっていたらやり直し（競合窓を最小化＋リトライ）。Drive v3 に厳密CASが無いための実装。
// mergeFn(現在のJSON or {}) は「書き込む完全なJSON」を返すこと（他項目を消さないよう現在値にマージする）。
export async function mergeUpdateJSON(name, mergeFn, maxRetries = 4) {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const existing = await findFile(name)
    let cur = {}
    let rev = null
    if (existing) {
      rev = await fileHeadRev(existing.id)
      try {
        const r = await driveGetResponse(existing.id)
        cur = await r.json()
      } catch {
        cur = {}
      }
    }
    const merged = await mergeFn(cur || {})
    if (existing) {
      const rev2 = await fileHeadRev(existing.id)
      if (rev != null && rev2 != null && rev2 !== rev) continue // 読んでから誰かが書いた → 再試行
    }
    await putJSON(name, merged)
    return merged
  }
  throw new Error('Drive の競合で更新できませんでした（時間をおいて再試行してください）')
}

// JSON を name で保存（あれば更新・無ければ Immerso フォルダに作成）。
export async function putJSON(name, obj) {
  const t = await token()
  const body = JSON.stringify(obj)
  const existing = await findFile(name)
  if (existing) {
    const r = await fetch(
      'https://www.googleapis.com/upload/drive/v3/files/' + existing.id + '?uploadType=media',
      {
        method: 'PATCH',
        headers: { Authorization: 'Bearer ' + t, 'Content-Type': 'application/json' },
        body,
      }
    )
    if (!r.ok) throw new Error('Drive 更新に失敗: ' + r.status)
  } else {
    const folderId = await ensureFolder()
    const boundary = 'immerso_' + Date.now() + '_' + Math.floor(Math.random() * 1e9)
    const multipart =
      '--' + boundary + '\r\n' +
      'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
      JSON.stringify({ name, parents: [folderId] }) + '\r\n' +
      '--' + boundary + '\r\n' +
      'Content-Type: application/json\r\n\r\n' +
      body + '\r\n' +
      '--' + boundary + '--'
    const r = await fetch(
      'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id',
      {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + t,
          'Content-Type': 'multipart/related; boundary=' + boundary,
        },
        body: multipart,
      }
    )
    if (!r.ok) throw new Error('Drive 作成に失敗: ' + r.status)
  }
  return { ok: true }
}
